import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import D3Dashboard from './D3Dashboard';
import api from './axiosConfig';
import '../styles/d3-styles.css';

// Variantes de animación
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Colores para cuartiles
const CUARTIL_COLORS = {
  'Q1': '#10b981', // emerald-500
  'Q2': '#3b82f6', // blue-500
  'Q3': '#f59e0b', // amber-500
  'Q4': '#f43f5e', // rose-500
  'Sin cuartil': '#94a3b8', // slate-400
};

const CUARTIL_BG = {
  'Q1': 'bg-emerald-500',
  'Q2': 'bg-blue-500',
  'Q3': 'bg-amber-500',
  'Q4': 'bg-rose-500',
  'Sin cuartil': 'bg-slate-400',
};

const chartTooltipStyle = {
  backgroundColor: 'rgba(15, 23, 42, 0.96)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '12px',
  color: '#ffffff',
};

const chartTooltipLabelStyle = { color: '#ffffff', fontWeight: 600 };

const chartTooltipItemStyle = { color: '#ffffff' };

const chartGridStroke = 'rgba(100, 116, 139, 0.35)';


/** KPI tile alineado con D3Dashboard (icono en gradiente + métrica) */
function ScimagoKpiCard({ icon, gradientClass, label, value, badge }) {
  return (
    <motion.div
      className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300"
      variants={fadeInUp}
      whileHover={{ y: -3 }}
    >
      <div className="absolute top-0 right-0 p-3 opacity-[0.08] group-hover:opacity-[0.14] transition-opacity pointer-events-none">
        <span className="material-icons-round text-6xl text-primary">{icon}</span>
      </div>
      <div className="flex items-center relative z-10">
        <div className={`w-11 h-11 rounded-xl ${gradientClass} flex items-center justify-center mr-3 shadow-lg shrink-0`}>
          <span className="material-icons-round text-white text-lg">{icon}</span>
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums truncate">{value}</div>
          <div className="text-xs font-medium text-slate-600 dark:text-slate-400">{label}</div>
        </div>
      </div>
      {badge && (
        <div className="mt-3 pt-3 border-t border-slate-200/80 dark:border-slate-700/50 relative z-10">
          {badge}
        </div>
      )}
    </motion.div>
  );
}

function Dashboards() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'scimago' | 'departamentos'
  
  // Estados para SCImago
  const [scimagoSummary, setScimagoSummary] = useState({ series: [], years: [], totals: null });
  const [universidades, setUniversidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroAnio, setFiltroAnio] = useState('todos');
  const [filtroUniversidad, setFiltroUniversidad] = useState('todas');

  // Estados para dashboard por departamento
  const [deptoOpciones, setDeptoOpciones] = useState([]);
  const [deptoInstitucionesOpts, setDeptoInstitucionesOpts] = useState([]);
  const [deptoInstitucion, setDeptoInstitucion] = useState('all');
  const [deptoSeleccionado, setDeptoSeleccionado] = useState('');
  const [deptoMetrics, setDeptoMetrics] = useState(null);
  const [deptoListLoading, setDeptoListLoading] = useState(false);
  const [deptoMetricsLoading, setDeptoMetricsLoading] = useState(false);
  const [deptoError, setDeptoError] = useState(null);

  useEffect(() => {
    if (activeTab === 'scimago') {
      fetchDatosScimago();
    }
  }, [activeTab, filtroUniversidad]);

  const fetchDeptoOpciones = async () => {
    try {
      setDeptoListLoading(true);
      const params = {};
      if (deptoInstitucion !== 'all') params.filtro_institucion = deptoInstitucion;
      const res = await api.get('/api/dashboard/departamento/opciones', { params });
      setDeptoOpciones(res.data?.items || []);
      setDeptoInstitucionesOpts(res.data?.filtros?.instituciones || []);
      setDeptoError(res.data?.error || null);
    } catch (err) {
      setDeptoError('No se pudieron cargar los departamentos');
      console.error(err);
    } finally {
      setDeptoListLoading(false);
    }
  };

  const fetchDeptoMetrics = async (nombreDepto) => {
    if (!nombreDepto) {
      setDeptoMetrics(null);
      return;
    }
    try {
      setDeptoMetricsLoading(true);
      const params = { departamento: nombreDepto, top_investigadores: 15 };
      if (deptoInstitucion !== 'all') params.filtro_institucion = deptoInstitucion;
      const res = await api.get('/api/dashboard/departamento/metrics', { params });
      setDeptoMetrics(res.data);
      setDeptoError(res.data?.error || null);
    } catch (err) {
      setDeptoMetrics(null);
      setDeptoError('Error cargando métricas del departamento');
      console.error(err);
    } finally {
      setDeptoMetricsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'departamentos') return;
    setDeptoSeleccionado('');
    setDeptoMetrics(null);
    fetchDeptoOpciones();
  }, [activeTab, deptoInstitucion]);

  useEffect(() => {
    if (activeTab !== 'departamentos' || !deptoSeleccionado) return;
    fetchDeptoMetrics(deptoSeleccionado);
  }, [deptoSeleccionado, activeTab, deptoInstitucion]);

  const fetchDatosScimago = async () => {
    try {
      setLoading(true);
      const summaryParams = {};
      if (filtroUniversidad !== 'todas') {
        summaryParams.universidad_id = filtroUniversidad;
      }

      const [summaryRes, universitiesRes] = await Promise.all([
        api.get('/api/dashboard/scimago-summary', { params: summaryParams }),
        api.get('/api/universities'),
      ]);

      setScimagoSummary(summaryRes.data || { series: [], years: [], totals: null });
      setUniversidades(Array.isArray(universitiesRes.data) ? universitiesRes.data : []);
      setError(null);
    } catch (err) {
      setError('Error cargando datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Filtrar publicaciones por año para SCImago
  const publicacionesFiltradas = useMemo(() => {
    const series = Array.isArray(scimagoSummary?.series) ? scimagoSummary.series : [];
    if (filtroAnio === 'todos') return series;
    return series.filter((row) => row.anio === parseInt(filtroAnio, 10));
  }, [scimagoSummary, filtroAnio]);

  // Obtener años únicos
  const anosDisponibles = useMemo(() => {
    return Array.isArray(scimagoSummary?.years) ? scimagoSummary.years : [];
  }, [scimagoSummary]);

  const universidadesDisponibles = useMemo(() => {
    return [...universidades]
      .filter((u) => u?.id != null)
      .sort((a, b) =>
        String(a.name || a.short_name || a.nombre_corto || '').localeCompare(
          String(b.name || b.short_name || b.nombre_corto || ''),
          'es',
          { sensitivity: 'base' }
        )
      );
  }, [universidades]);

  // Datos para gráfico de pastel
  const datosPie = useMemo(() => {
    const counts = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, 'Sin cuartil': 0 };
    publicacionesFiltradas.forEach((row) => {
      counts.Q1 += Number(row.Q1 || 0);
      counts.Q2 += Number(row.Q2 || 0);
      counts.Q3 += Number(row.Q3 || 0);
      counts.Q4 += Number(row.Q4 || 0);
      counts['Sin cuartil'] += Number(row.sin_cuartil || 0);
    });
    return Object.entries(counts)
      .filter(([_, value]) => value > 0)
      .map(([name, value]) => ({ name, value }));
  }, [publicacionesFiltradas]);

  // Datos por año (tendencias)
  const datosTendencia = useMemo(() => {
    return (Array.isArray(scimagoSummary?.series) ? scimagoSummary.series : []).map((row) => ({
      anio: row.anio,
      Q1: Number(row.Q1 || 0),
      Q2: Number(row.Q2 || 0),
      Q3: Number(row.Q3 || 0),
      Q4: Number(row.Q4 || 0),
      total: Number(row.total || 0),
    }));
  }, [scimagoSummary]);

  // Métricas calculadas
  const metricas = useMemo(() => {
    const total = publicacionesFiltradas.reduce((acc, row) => acc + Number(row.total || 0), 0);
    const q1 = publicacionesFiltradas.reduce((acc, row) => acc + Number(row.Q1 || 0), 0);
    const q2 = publicacionesFiltradas.reduce((acc, row) => acc + Number(row.Q2 || 0), 0);
    const q3 = publicacionesFiltradas.reduce((acc, row) => acc + Number(row.Q3 || 0), 0);
    const q4 = publicacionesFiltradas.reduce((acc, row) => acc + Number(row.Q4 || 0), 0);
    const conCuartil = q1 + q2 + q3 + q4;
    const q1q2 = q1 + q2;
    
    return {
      total,
      conCuartil,
      sinCuartil: total - conCuartil,
      cobertura: total > 0 ? ((conCuartil / total) * 100).toFixed(1) : 0,
      q1q2,
      porcentajeQ1Q2: total > 0 ? ((q1q2 / total) * 100).toFixed(1) : 0,
    };
  }, [publicacionesFiltradas]);

  return (
    <>
      <div className="flex-1 overflow-y-auto p-3 sm:p-5 lg:p-8 space-y-6 sm:space-y-8">
        {/* Tabs de navegación */}
        <motion.div 
          className="flex gap-2 overflow-x-auto border-b border-slate-200 dark:border-slate-700 pb-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            onClick={() => setActiveTab('general')}
            className={`shrink-0 whitespace-nowrap rounded-t-lg px-3 sm:px-4 py-3 flex items-center gap-2 border-b-2 transition-colors font-medium ${
              activeTab === 'general'
                ? 'border-[#2D5BA6] text-[#2D5BA6]'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
          >
            <span className="material-icons-round text-sm">analytics</span>
            Dashboard General
          </button>
          <button
            onClick={() => setActiveTab('scimago')}
            className={`shrink-0 whitespace-nowrap rounded-t-lg px-3 sm:px-4 py-3 flex items-center gap-2 border-b-2 transition-colors font-medium ${
              activeTab === 'scimago'
                ? 'border-[#2D5BA6] text-[#2D5BA6]'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
          >
            <span className="material-icons-round text-sm">trending_up</span>
            SCImago Cuartiles
          </button>
          <button
            onClick={() => setActiveTab('departamentos')}
            className={`shrink-0 whitespace-nowrap rounded-t-lg px-3 sm:px-4 py-3 flex items-center gap-2 border-b-2 transition-colors font-medium ${
              activeTab === 'departamentos'
                ? 'border-[#2D5BA6] text-[#2D5BA6]'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
            }`}
          >
            <span className="material-icons-round text-sm">domain</span>
            Por departamento
          </button>
        </motion.div>

        {activeTab === 'general' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6 sm:space-y-8"
          >
            {/* Stats rápidas con diseño mejorado */}
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 px-1">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Métricas del Dashboard</h3>
                <motion.span 
                  className="text-xs text-slate-400 dark:text-slate-500"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Actualizado en tiempo real
                </motion.span>
              </div>
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.02, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-primary">analytics</span>
                  </div>
                  <div className="flex items-center relative z-10">
                    <div className="flex-shrink-0">
                      <motion.div 
                        className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-lg"
                        whileHover={{ rotate: 10 }}
                      >
                        <span className="material-icons-round text-white text-xl">analytics</span>
                      </motion.div>
                    </div>
                    <div className="ml-4">
                      <motion.div 
                        className="text-2xl font-bold text-slate-900 dark:text-white"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                      >
                        6
                      </motion.div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">Gráficos Interactivos</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700/50">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      <motion.span 
                        className="material-icons-round text-xs mr-1"
                        animate={{ y: [0, -2, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        trending_up
                      </motion.span>
                      Activo
                    </span>
                  </div>
                </motion.div>

                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.02, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-emerald-500">speed</span>
                  </div>
                  <div className="flex items-center relative z-10">
                    <div className="flex-shrink-0">
                      <motion.div 
                        className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg"
                        whileHover={{ rotate: 10 }}
                      >
                        <span className="material-icons-round text-white text-xl">speed</span>
                      </motion.div>
                    </div>
                    <div className="ml-4">
                      <motion.div 
                        className="text-2xl font-bold text-slate-900 dark:text-white"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
                      >
                        4
                      </motion.div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">KPIs en Tiempo Real</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700/50">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      <motion.span 
                        className="material-icons-round text-xs mr-1"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        refresh
                      </motion.span>
                      Live
                    </span>
                  </div>
                </motion.div>

                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.02, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-amber-500">filter_alt</span>
                  </div>
                  <div className="flex items-center relative z-10">
                    <div className="flex-shrink-0">
                      <motion.div 
                        className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg"
                        whileHover={{ rotate: 10 }}
                      >
                        <span className="material-icons-round text-white text-xl">filter_alt</span>
                      </motion.div>
                    </div>
                    <div className="ml-4">
                      <motion.div 
                        className="text-2xl font-bold text-slate-900 dark:text-white"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
                      >
                        2
                      </motion.div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">Filtros Dinámicos</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700/50">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                      <span className="material-icons-round text-xs mr-1">tune</span>
                      Configurado
                    </span>
                  </div>
                </motion.div>

                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={fadeInUp}
                  whileHover={{ scale: 1.02, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-purple-500">insights</span>
                  </div>
                  <div className="flex items-center relative z-10">
                    <div className="flex-shrink-0">
                      <motion.div 
                        className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg"
                        whileHover={{ rotate: 10 }}
                      >
                        <span className="material-icons-round text-white text-xl">insights</span>
                      </motion.div>
                    </div>
                    <div className="ml-4">
                      <motion.div 
                        className="text-2xl font-bold text-slate-900 dark:text-white"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.5 }}
                      >
                        ∞
                      </motion.div>
                      <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">Análisis Avanzado</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700/50">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                      <span className="material-icons-round text-xs mr-1">auto_graph</span>
                      Disponible
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            </motion.section>

            {/* Dashboard D3.js */}
            <motion.section 
              className="relative"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 px-1">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Visualizaciones Interactivas</h3>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    <span className="material-icons-round text-xs mr-1">auto_graph</span>
                    D3.js
                  </span>
                  <motion.button 
                    onClick={handleRefresh}
                    className="inline-flex items-center px-3 py-1.5 border border-slate-200 dark:border-slate-600 text-xs font-medium rounded-lg text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary hover:border-primary/50 transition-all bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.span 
                      className="material-icons-round text-sm mr-1"
                      whileHover={{ rotate: 180 }}
                      transition={{ duration: 0.3 }}
                    >
                      refresh
                    </motion.span>
                    Actualizar
                  </motion.button>
                </div>
              </div>
              <motion.div 
                className="glass-panel rounded-2xl border border-white/20 shadow-2xl dark:border-slate-700 overflow-hidden relative"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-50"></div>
                <div className="relative p-4 sm:p-6">
                  <D3Dashboard key={refreshKey} />
                </div>
              </motion.div>
            </motion.section>

            {/* Info adicional mejorada */}
            <section>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-6 px-1">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Características del Sistema</h3>
                <span className="text-xs text-slate-400 dark:text-slate-500">Tecnología Plotly Dash</span>
              </div>
              <div className="glass-panel rounded-xl border border-white/20 shadow-lg dark:border-slate-700 p-4 sm:p-6 lg:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5">
                  <span className="material-icons-round text-8xl text-primary">info</span>
                </div>
                <div className="relative z-10">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mr-3 shadow-lg">
                      <span className="material-icons-round text-white">info</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Capacidades del Dashboard</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* ... características ... */}
                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-blue-500/10 rounded-xl transform rotate-1 group-hover:rotate-2 transition-transform"></div>
                      <div className="relative p-6 bg-white/70 dark:bg-slate-800/70 rounded-xl border border-primary/20 shadow-sm backdrop-blur-sm">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center mr-2">
                            <span className="material-icons-round text-white text-sm">filter_list</span>
                          </div>
                          <h4 className="font-semibold text-slate-900 dark:text-white">Filtros Dinámicos</h4>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                          Filtra datos por institución y área de conocimiento en tiempo real
                        </p>
                      </div>
                    </div>

                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 to-indigo-500/10 rounded-xl transform rotate-1 group-hover:rotate-2 transition-transform"></div>
                      <div className="relative p-6 bg-white/70 dark:bg-slate-800/70 rounded-xl border border-secondary/20 shadow-sm backdrop-blur-sm">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-secondary to-indigo-600 flex items-center justify-center mr-2">
                            <span className="material-icons-round text-white text-sm">speed</span>
                          </div>
                          <h4 className="font-semibold text-slate-900 dark:text-white">KPIs en Tiempo Real</h4>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                          Métricas actualizadas: publicaciones, investigadores, impact factor promedio
                        </p>
                      </div>
                    </div>

                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl transform rotate-1 group-hover:rotate-2 transition-transform"></div>
                      <div className="relative p-6 bg-white/70 dark:bg-slate-800/70 rounded-xl border border-emerald-500/20 shadow-sm backdrop-blur-sm">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mr-2">
                            <span className="material-icons-round text-white text-sm">bar_chart</span>
                          </div>
                          <h4 className="font-semibold text-slate-900 dark:text-white">Visualizaciones</h4>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                          Gráficos avanzados: barras, líneas, pie charts y análisis de tendencias
                        </p>
                      </div>
                    </div>

                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl transform rotate-1 group-hover:rotate-2 transition-transform"></div>
                      <div className="relative p-6 bg-white/70 dark:bg-slate-800/70 rounded-xl border border-amber-500/20 shadow-sm backdrop-blur-sm">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mr-2">
                            <span className="material-icons-round text-white text-sm">emoji_events</span>
                          </div>
                          <h4 className="font-semibold text-slate-900 dark:text-white">Top Rankings</h4>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                          Rankings de los 10 investigadores más productivos y colaboraciones
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </motion.div>
        )}

        {activeTab === 'scimago' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6 sm:space-y-8"
          >
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <span className="material-icons-round text-primary text-6xl animate-spin">refresh</span>
                  <p className="text-slate-500 dark:text-slate-400 mt-4">Cargando datos SCImago…</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center glass-panel rounded-2xl border border-white/20 dark:border-slate-700 px-10 py-12 shadow-xl max-w-md">
                  <span className="material-icons-round text-red-400 text-6xl">error</span>
                  <p className="text-red-600 dark:text-red-300 mt-4 font-medium">{error}</p>
                  <motion.button
                    type="button"
                    onClick={fetchDatosScimago}
                    className="mt-6 inline-flex items-center px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium shadow-lg"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="material-icons-round text-sm mr-1">refresh</span>
                    Reintentar
                  </motion.button>
                </div>
              </div>
            ) : (
              <>
                <motion.section
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 px-1">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Métricas SCImago / Cuartiles
                    </h3>
                    <span className="text-xs text-slate-400 dark:text-slate-500">Basado en resumen agregado del servidor</span>
                  </div>
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    <ScimagoKpiCard
                      icon="article"
                      gradientClass="bg-gradient-to-br from-primary to-blue-600"
                      label="Publicaciones (filtro)"
                      value={metricas.total}
                      badge={
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          <span className="material-icons-round text-xs mr-1">filter_alt</span>
                          {filtroAnio === 'todos' ? 'Todos los años' : `Año ${filtroAnio}`}
                        </span>
                      }
                    />
                    <ScimagoKpiCard
                      icon="verified"
                      gradientClass="bg-gradient-to-br from-emerald-500 to-teal-600"
                      label="Cobertura SCImago"
                      value={`${metricas.cobertura}%`}
                      badge={
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                          {metricas.conCuartil} con cuartil asignado
                        </span>
                      }
                    />
                    <ScimagoKpiCard
                      icon="trending_up"
                      gradientClass="bg-gradient-to-br from-secondary to-indigo-600"
                      label="Q1 + Q2"
                      value={metricas.q1q2}
                      badge={
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                          Alto impacto relativo
                        </span>
                      }
                    />
                    <ScimagoKpiCard
                      icon="percent"
                      gradientClass="bg-gradient-to-br from-amber-500 to-orange-600"
                      label="% en Q1 + Q2"
                      value={`${metricas.porcentajeQ1Q2}%`}
                      badge={
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          Del total filtrado
                        </span>
                      }
                    />
                  </motion.div>
                </motion.section>

                <motion.div
                  className="bg-surface-light dark:bg-surface-dark rounded-xl p-4 sm:p-6 border border-border-light dark:border-border-dark shadow-sm"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 items-end">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Filtrar por institución
                      </label>
                      <select
                        value={filtroUniversidad}
                        onChange={(e) => setFiltroUniversidad(e.target.value)}
                        className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="todas">Todas las universidades</option>
                        {universidadesDisponibles.map((universidad) => (
                          <option key={universidad.id} value={String(universidad.id)}>
                            {universidad.name || universidad.short_name || universidad.nombre_corto || `IES ${universidad.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Filtrar por año
                      </label>
                      <select
                        value={filtroAnio}
                        onChange={(e) => setFiltroAnio(e.target.value)}
                        className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="todos">Todos los años</option>
                        {anosDisponibles.map((a) => (
                          <option key={a} value={String(a)}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 lg:pb-2">
                      Las tarjetas superiores y el pastel respetan este filtro; la evolución temporal usa el conjunto completo.
                    </p>
                  </div>
                </motion.div>

                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <div className="flex items-center justify-between mb-6 px-1 flex-wrap gap-3">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Visualizaciones
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                        <span className="material-icons-round text-xs mr-1">stacked_bar_chart</span>
                        Recharts
                      </span>
                      <motion.button
                        type="button"
                        onClick={fetchDatosScimago}
                        className="inline-flex items-center px-3 py-1.5 border border-slate-200 dark:border-slate-600 text-xs font-medium rounded-lg text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary hover:border-primary/50 transition-all bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="material-icons-round text-sm mr-1">refresh</span>
                        Actualizar
                      </motion.button>
                    </div>
                  </div>

                  <motion.div
                    className="glass-panel rounded-2xl border border-white/20 shadow-2xl dark:border-slate-700 overflow-hidden relative mb-6"
                    initial={{ opacity: 0, scale: 0.99 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-50 pointer-events-none" />
                    <div className="relative p-6 space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-white/40 dark:bg-slate-900/30">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                            <span className="material-icons-round mr-2 text-emerald-500">pie_chart</span>
                            Distribución por cuartil
                          </h3>
                          <div className="d3-chart-container w-full min-h-[300px]">
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={datosPie}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                  outerRadius={100}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {datosPie.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={CUARTIL_COLORS[entry.name] || '#94a3b8'} />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={chartTooltipStyle}
                                  labelStyle={chartTooltipLabelStyle}
                                  itemStyle={chartTooltipItemStyle}
                                />
                                <Legend
                                  wrapperStyle={{ fontSize: '12px', color: '#1e293b' }}
                                  className="recharts-scimago-legend"
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-white/40 dark:bg-slate-900/30">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                            <span className="material-icons-round mr-2 text-primary">bar_chart</span>
                            Comparación por cuartil
                          </h3>
                          <div className="d3-chart-container w-full min-h-[300px]">
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart data={datosPie} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                                <XAxis type="number" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                                <YAxis
                                  dataKey="name"
                                  type="category"
                                  width={88}
                                  stroke="#94a3b8"
                                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                                />
                                <Tooltip
                                  contentStyle={chartTooltipStyle}
                                  labelStyle={chartTooltipLabelStyle}
                                  itemStyle={chartTooltipItemStyle}
                                />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                  {datosPie.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={CUARTIL_COLORS[entry.name] || '#94a3b8'} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>

                      {datosTendencia.length > 0 && (
                        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-white/40 dark:bg-slate-900/30">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                            <span className="material-icons-round mr-2 text-amber-500">area_chart</span>
                            Evolución por cuartil (todos los años)
                          </h3>
                          <div className="d3-chart-container w-full min-h-[300px]">
                            <ResponsiveContainer width="100%" height={320}>
                              <AreaChart data={datosTendencia}>
                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                                <XAxis dataKey="anio" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                                <Tooltip
                                  contentStyle={chartTooltipStyle}
                                  labelStyle={chartTooltipLabelStyle}
                                  itemStyle={chartTooltipItemStyle}
                                />
                                <Legend
                                  wrapperStyle={{ fontSize: '12px', color: '#1e293b' }}
                                  className="recharts-scimago-legend"
                                />
                                <Area type="monotone" dataKey="Q1" stackId="1" stroke={CUARTIL_COLORS.Q1} fill={CUARTIL_COLORS.Q1} />
                                <Area type="monotone" dataKey="Q2" stackId="1" stroke={CUARTIL_COLORS.Q2} fill={CUARTIL_COLORS.Q2} />
                                <Area type="monotone" dataKey="Q3" stackId="1" stroke={CUARTIL_COLORS.Q3} fill={CUARTIL_COLORS.Q3} />
                                <Area type="monotone" dataKey="Q4" stackId="1" stroke={CUARTIL_COLORS.Q4} fill={CUARTIL_COLORS.Q4} />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.section>

                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  <div className="glass-panel rounded-xl border border-white/20 shadow-lg dark:border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/80 flex items-center gap-3 bg-white/30 dark:bg-slate-900/20">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-md">
                        <span className="material-icons-round text-white text-lg">table_chart</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Resumen por cuartil</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Cantidades según el filtro de año activo</p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[520px]">
                        <thead>
                          <tr className="bg-slate-50/90 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                              Cuartil
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                              Cantidad
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                              %
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide min-w-[140px]">
                              Distribución
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200/80 dark:divide-slate-700/80">
                          {['Q1', 'Q2', 'Q3', 'Q4', 'Sin cuartil'].map((cuartil) => {
                            const keyPie = cuartil === 'Sin cuartil' ? 'Sin cuartil' : cuartil;
                            const count = datosPie.find((d) => d.name === keyPie)?.value || 0;
                            const pct = metricas.total > 0 ? ((count / metricas.total) * 100).toFixed(1) : 0;
                            return (
                              <tr
                                key={cuartil}
                                className="transition-colors hover:bg-primary hover:[&_td]:text-white dark:hover:bg-primary dark:hover:[&_td]:text-white"
                              >
                                <td className="px-6 py-4">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-bold ${
                                      cuartil === 'Q1'
                                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                        : cuartil === 'Q2'
                                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                                          : cuartil === 'Q3'
                                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                                            : cuartil === 'Q4'
                                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
                                              : 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-200'
                                    }`}
                                  >
                                    {cuartil}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-center font-medium text-slate-800 dark:text-slate-200">{count}</td>
                                <td className="px-6 py-4 text-center text-slate-600 dark:text-slate-400 tabular-nums">{pct}%</td>
                                <td className="px-6 py-4">
                                  <div className="w-full max-w-[200px] mx-auto bg-slate-200/80 dark:bg-slate-700/80 rounded-full h-2 overflow-hidden">
                                    <div
                                      className={`h-2 rounded-full transition-all duration-500 ${CUARTIL_BG[keyPie] || 'bg-slate-400'}`}
                                      style={{ width: `${Math.min(100, Number(pct))}%` }}
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </motion.section>

                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-6 px-1">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      ¿Qué muestra este tablero?
                    </h3>
                    <span className="text-xs text-slate-400 dark:text-slate-500">SCImago / SJR</span>
                  </div>
                  <div className="glass-panel rounded-xl border border-white/20 shadow-lg dark:border-slate-700 p-4 sm:p-6 lg:p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-[0.06] pointer-events-none">
                      <span className="material-icons-round text-8xl text-primary">insights</span>
                    </div>
                    <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {[
                        {
                          t: 'Cobertura',
                          d: 'Porcentaje de publicaciones con cuartil asignado respecto al total filtrado.',
                          icon: 'track_changes',
                          g: 'from-primary to-blue-600',
                        },
                        {
                          t: 'Q1 y Q2',
                          d: 'Conteo y proporción de trabajos en revistas de primer y segundo cuartil.',
                          icon: 'military_tech',
                          g: 'from-emerald-500 to-teal-600',
                        },
                        {
                          t: 'Filtro por año',
                          d: 'Aisla un año para KPIs, pastel y barras; la serie temporal sigue siendo global.',
                          icon: 'date_range',
                          g: 'from-amber-500 to-orange-600',
                        },
                        {
                          t: 'Datos',
                          d: 'Los cuartiles provienen del campo asignado a cada publicación en el sistema.',
                          icon: 'database',
                          g: 'from-purple-500 to-pink-600',
                        },
                      ].map((item) => (
                        <div key={item.t} className="group relative">
                          <div
                            className={`absolute inset-0 bg-gradient-to-br ${item.g} opacity-10 rounded-xl transform rotate-1 group-hover:rotate-2 transition-transform`}
                          />
                          <div className="relative p-6 bg-white/70 dark:bg-slate-800/70 rounded-xl border border-white/40 dark:border-slate-600/50 shadow-sm backdrop-blur-sm">
                            <div className="flex items-center mb-3">
                              <div
                                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.g} flex items-center justify-center mr-2 shadow-md`}
                              >
                                <span className="material-icons-round text-white text-sm">{item.icon}</span>
                              </div>
                              <h4 className="font-semibold text-slate-900 dark:text-white">{item.t}</h4>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{item.d}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.section>
              </>
            )}
          </motion.div>
        )}

        {activeTab === 'departamentos' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <motion.div
              className="bg-surface-light dark:bg-surface-dark rounded-xl p-4 sm:p-6 border border-border-light dark:border-border-dark shadow-sm"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Institución (opcional)
                  </label>
                  <select
                    value={deptoInstitucion}
                    onChange={(e) => {
                      setDeptoInstitucion(e.target.value);
                      setDeptoSeleccionado('');
                      setDeptoMetrics(null);
                    }}
                    className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="all">Todas las instituciones</option>
                    {deptoInstitucionesOpts.map((inst) => (
                      <option key={inst} value={inst}>
                        {inst}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Departamento
                  </label>
                  <select
                    value={deptoSeleccionado}
                    onChange={(e) => setDeptoSeleccionado(e.target.value)}
                    disabled={deptoListLoading || deptoOpciones.length === 0}
                    className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60"
                  >
                    <option value="">
                      {deptoListLoading ? 'Cargando…' : 'Selecciona un departamento'}
                    </option>
                    {deptoOpciones.map((row) => (
                      <option key={row.departamento} value={row.departamento}>
                        {row.departamento} — {row.publicaciones} pub. · {row.investigadores} inv.
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {deptoError && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">{deptoError}</p>
              )}
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Los datos se basan en investigadores con campo departamento y en publicaciones vinculadas en el sistema.
              </p>
            </motion.div>

            {deptoListLoading && !deptoSeleccionado ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <span className="material-icons-round text-primary text-5xl animate-spin">refresh</span>
                  <p className="text-slate-500 dark:text-slate-400 mt-3">Cargando departamentos…</p>
                </div>
              </div>
            ) : null}

            {deptoSeleccionado && deptoMetrics?.empty && !deptoMetricsLoading ? (
              <div className="glass-panel rounded-xl border border-white/20 dark:border-slate-700 p-8 text-center">
                <span className="material-icons-round text-amber-500 text-5xl">folder_off</span>
                <p className="mt-4 text-slate-600 dark:text-slate-300">
                  No hay investigadores con departamento «{deptoSeleccionado}» con los filtros actuales.
                </p>
              </div>
            ) : null}

            {deptoSeleccionado && deptoMetrics && !deptoMetrics.empty && (
              <>
                <motion.section
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center justify-between mb-4 px-1 flex-wrap gap-2">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      {deptoMetrics.departamento}
                    </h3>
                    {deptoMetricsLoading && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <span className="material-icons-round text-sm animate-spin">refresh</span>
                        Actualizando…
                      </span>
                    )}
                  </div>
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    <ScimagoKpiCard
                      icon="groups"
                      gradientClass="bg-gradient-to-br from-primary to-blue-600"
                      label="Investigadores"
                      value={deptoMetrics.kpis?.total_investigadores ?? 0}
                    />
                    <ScimagoKpiCard
                      icon="article"
                      gradientClass="bg-gradient-to-br from-emerald-500 to-teal-600"
                      label="Publicaciones vinculadas"
                      value={deptoMetrics.kpis?.total_publicaciones ?? 0}
                    />
                    <ScimagoKpiCard
                      icon="verified"
                      gradientClass="bg-gradient-to-br from-secondary to-indigo-600"
                      label="Cobertura cuartil"
                      value={`${deptoMetrics.kpis?.cobertura_cuartil_pct ?? 0}%`}
                      badge={
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                          {deptoMetrics.kpis?.con_cuartil ?? 0} con Q1–Q4
                        </span>
                      }
                    />
                    <ScimagoKpiCard
                      icon="military_tech"
                      gradientClass="bg-gradient-to-br from-amber-500 to-orange-600"
                      label="Q1 + Q2"
                      value={`${deptoMetrics.kpis?.pct_q1_q2 ?? 0}%`}
                      badge={
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          {deptoMetrics.kpis?.q1_q2 ?? 0} publicaciones
                        </span>
                      }
                    />
                  </motion.div>
                </motion.section>

                <motion.div
                  className="glass-panel rounded-2xl border border-white/20 shadow-2xl dark:border-slate-700 overflow-hidden relative"
                  initial={{ opacity: 0, scale: 0.99 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-50 pointer-events-none" />
                  <div className="relative p-6 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-white/40 dark:bg-slate-900/30">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                          <span className="material-icons-round mr-2 text-emerald-500">pie_chart</span>
                          Publicaciones por cuartil
                        </h3>
                        <div className="d3-chart-container w-full min-h-[280px]">
                          {(deptoMetrics.cuartiles_distribucion || []).length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie
                                  data={deptoMetrics.cuartiles_distribucion}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) =>
                                    `${name}: ${(percent * 100).toFixed(0)}%`
                                  }
                                  outerRadius={95}
                                  dataKey="value"
                                >
                                  {(deptoMetrics.cuartiles_distribucion || []).map((entry, index) => (
                                    <Cell
                                      key={`cell-d-${index}`}
                                      fill={CUARTIL_COLORS[entry.name] || '#94a3b8'}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={chartTooltipStyle}
                                  labelStyle={chartTooltipLabelStyle}
                                  itemStyle={chartTooltipItemStyle}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400 py-12 text-center">
                              Sin datos de cuartil para este departamento.
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-white/40 dark:bg-slate-900/30">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                          <span className="material-icons-round mr-2 text-primary">bar_chart</span>
                          Artículos por año
                        </h3>
                        <div className="d3-chart-container w-full min-h-[280px]">
                          {(deptoMetrics.publicaciones_por_anio || []).length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={deptoMetrics.publicaciones_por_anio}>
                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                                <XAxis
                                  dataKey="anio"
                                  stroke="#94a3b8"
                                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                                />
                                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                                <Tooltip
                                  contentStyle={chartTooltipStyle}
                                  labelStyle={chartTooltipLabelStyle}
                                  itemStyle={chartTooltipItemStyle}
                                />
                                <Bar dataKey="cantidad" fill="#2D5BA6" radius={[4, 4, 0, 0]} name="Publicaciones" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-sm text-slate-500 dark:text-slate-400 py-12 text-center">
                              No hay años con publicaciones registradas.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {(deptoMetrics.publicaciones_por_anio_cuartil || []).length > 0 && (
                      <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 bg-white/40 dark:bg-slate-900/30">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
                          <span className="material-icons-round mr-2 text-amber-500">stacked_bar_chart</span>
                          Evolución por año y cuartil (Q1–Q4)
                        </h3>
                        <div className="d3-chart-container w-full min-h-[300px]">
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={deptoMetrics.publicaciones_por_anio_cuartil}>
                              <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                              <XAxis
                                dataKey="anio"
                                stroke="#94a3b8"
                                tick={{ fill: '#94a3b8', fontSize: 11 }}
                              />
                              <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                              <Tooltip
                                contentStyle={chartTooltipStyle}
                                labelStyle={chartTooltipLabelStyle}
                                itemStyle={chartTooltipItemStyle}
                              />
                              <Legend
                                wrapperStyle={{ fontSize: '12px' }}
                                className="recharts-scimago-legend"
                              />
                              <Bar dataKey="Q1" stackId="q" fill={CUARTIL_COLORS.Q1} />
                              <Bar dataKey="Q2" stackId="q" fill={CUARTIL_COLORS.Q2} />
                              <Bar dataKey="Q3" stackId="q" fill={CUARTIL_COLORS.Q3} />
                              <Bar dataKey="Q4" stackId="q" fill={CUARTIL_COLORS.Q4} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 px-1">
                          Solo se apilan publicaciones con cuartil Q1–Q4; el resto cuenta en el total general y en el gráfico circular.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <motion.div
                    className="glass-panel rounded-xl border border-white/20 shadow-lg dark:border-slate-700 overflow-hidden"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/80 flex items-center gap-3 bg-white/30 dark:bg-slate-900/20">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-md">
                        <span className="material-icons-round text-white text-lg">emoji_events</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          Mejores investigadores
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Por número de publicaciones vinculadas al departamento
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                      <table className="w-full min-w-[320px]">
                        <thead className="sticky top-0 bg-slate-50/95 dark:bg-slate-800/95 z-10">
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                              #
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                              Nombre
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                              Pubs.
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">
                              Citas (perfil)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200/80 dark:divide-slate-700/80">
                          {(deptoMetrics.top_investigadores || []).map((inv, idx) => (
                            <tr
                              key={inv.id}
                              className="hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                            >
                              <td className="px-4 py-3 text-slate-500 tabular-nums">{idx + 1}</td>
                              <td className="px-4 py-3 text-slate-800 dark:text-slate-200 font-medium">
                                {inv.nombre}
                              </td>
                              <td className="px-4 py-3 text-center tabular-nums text-slate-700 dark:text-slate-300">
                                {inv.publicaciones}
                              </td>
                              <td className="px-4 py-3 text-center tabular-nums text-slate-600 dark:text-slate-400">
                                {inv.citas_perfil}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(deptoMetrics.top_investigadores || []).length === 0 && (
                        <p className="p-6 text-sm text-slate-500 text-center">Sin datos.</p>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    className="glass-panel rounded-xl border border-white/20 shadow-lg dark:border-slate-700 overflow-hidden"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <div className="px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/80 flex items-center gap-3 bg-white/30 dark:bg-slate-900/20">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-indigo-600 flex items-center justify-center shadow-md">
                        <span className="material-icons-round text-white text-lg">category</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          Áreas de conocimiento
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          En publicaciones del departamento
                        </p>
                      </div>
                    </div>
                    <div className="p-4 max-h-[360px] overflow-y-auto">
                      {(deptoMetrics.areas_conocimiento || []).length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={[...(deptoMetrics.areas_conocimiento || [])].reverse()}
                            layout="vertical"
                            margin={{ left: 8, right: 16 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                            <XAxis type="number" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                            <YAxis
                              dataKey="area"
                              type="category"
                              width={120}
                              stroke="#94a3b8"
                              tick={{ fill: '#94a3b8', fontSize: 10 }}
                            />
                            <Tooltip
                              contentStyle={chartTooltipStyle}
                              labelStyle={chartTooltipLabelStyle}
                              itemStyle={chartTooltipItemStyle}
                            />
                            <Bar dataKey="cantidad" fill="#6366f1" radius={[0, 4, 4, 0]} name="Publicaciones" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
                          Sin áreas clasificadas.
                        </p>
                      )}
                    </div>
                  </motion.div>
                </div>
              </>
            )}

            {!deptoSeleccionado && !deptoListLoading && deptoOpciones.length === 0 && (
              <div className="glass-panel rounded-xl border border-white/20 dark:border-slate-700 p-8 text-center">
                <span className="material-icons-round text-slate-400 text-5xl">inventory_2</span>
                <p className="mt-4 text-slate-600 dark:text-slate-300">
                  No hay departamentos con investigadores en los datos actuales. Revisa que el campo
                  departamento esté cargado en investigadores.
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </>
  );
}

export default Dashboards;
